import { Component, OnInit } from '@angular/core';
import { SharedService } from '@services/shared.service';
import { ApiAccountService } from '@services/api-account.service';
import { RegisterEmailModel, RegisterModel, RegisterVerificationModel } from '@shared/models/RegisterModel';
import { ApiCustomerService } from '@services/api-customer.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormBase } from '@shared/form-base.component';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { CustomerModel } from '@shared/models/CustomerModel';
import { SessionManager } from '@services/session-manager';

@Component({
	selector: 'app-verify-email-form',
	templateUrl: './verify-email-form.component.html',
	styleUrls: ['./verify-email-form.component.scss']
})
export class VerifyEmailFormComponent extends FormBase<RegisterModel> implements OnInit {

	user: CustomerModel;

	// Se utiliza para manejar el estado de la verificación
	isVerificationSuccess = false;
	inviteCode: string = null;
	emailDuplicated = false;

	// form
	registerForm: UntypedFormGroup;
	codeAlert: string;
	requestAt: Date;
	// Evento para ejecutar en el componente hijo
	onReset = new Subject<boolean>()

	constructor(private router: Router, public sharedService: SharedService, private apiAccountService: ApiAccountService, private apiCustomerService: ApiCustomerService, private formBuilder: UntypedFormBuilder) {
		super();
	}

	ngOnInit() {

		const session = this.apiAccountService.getSession();
		if (session && session.user) {
			this.user = session.user;
			this._formInit();
		} else {
			this.user = null
			this.apiAccountService.logout(this.router);
		}

	}

	// Form

	isInvalid(): boolean {

		// Es invalido si el email ya existe
		if (this.emailDuplicated) return true;

		// Si los datos son validos compro var si la verificación se envío
		const invalid = this.registerForm.invalid;
		if (invalid && !this.isVerificationSuccess) {
			return true;
		} else if (this.isVerificationSuccess) {
			if (this.email.value?.length) {
				return false;
			}
			return true;
		}
	}

	isInvalidCode(): boolean {

		return this.codeConfirmation?.value?.length != 6;
	}

	get email() { return this._formGet(this.registerForm, 'email'); }
	get invalidEmail(): boolean {
		if (this.email && this.email.value) {
			this.email.setValue(this.email.value?.trim());
		}
		return this._isInvalid(this.registerForm, 'email', this.touched);
	}

	get codeConfirmation() { return this._formGet(this.registerForm, 'codeConfirmation'); }
	get invalidCodeConfirmation(): boolean {
		return this._isInvalid(this.registerForm, 'codeConfirmation', this.touched);
	}

	private _formInit(): void {

		// Reset for hide form errors
		this.touched = true;

		this.registerForm = this.formBuilder.group(
			{
				email: new UntypedFormControl(
					this.user.email,
					[Validators.required, Validators.email]
				),
				codeConfirmation: new UntypedFormControl(
					'',
					[]
				)
			},
		);
	}

	onCheckEmail() {

		if (this.invalidEmail) return;

		this.emailDuplicated = false;

		const registerModel = new RegisterModel({
			email: this.email.value.toLowerCase().trim(),
		});

		this.findCustomerByEmail(registerModel);
	}

	findCustomerByEmail(registerModel: RegisterModel): void {

		this.emailDuplicated = false;

		this.apiCustomerService.findCustomerByEmail(registerModel.email, true).then(
			(customerName: string) => {

				//if the call succeeds the email was already registered
				this.emailDuplicated = true;

			}, (err) => {

				//If the call returns a not found means that the email wasnt registered yet
				this.emailDuplicated = false;
			}
		);
	}


	onVerifyEmail(showAlert = false): void {

		if (this.invalidEmail) return;

		this.requestAt = new Date();

		const registerModel = new RegisterModel({
			email: this.email.value?.trim(),
			requestAt: this.requestAt
		});

		if (this.emailDuplicated) return;

		this.apiAccountService.verifyEmail(registerModel).then(
			() => {

				this.isVerificationSuccess = true;
				this.emailDuplicated = false;

				this.sharedService.setLoading(false);
			},
			(error: any) => {

				this.isVerificationSuccess = false;

				this.sharedService.setLoading(false); // hide loader

				if (typeof error?.error == "string") {

					if (error?.error.toLowerCase().indexOf("vinculado") >= 0) {
						this.emailDuplicated = true;
						const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
						this.sharedService.showErrorAlert(null, errMsg);
					} else if (error?.error.toLowerCase().indexOf("requerido") >= 0) {
						const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
						this.sharedService.showErrorAlert(null, errMsg);
					}
				} else {
					// display error
					const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error verificando el email.');
					this.sharedService.showErrorAlert(null, errMsg);
				}
			}
		);

		if (showAlert) {
			// Mostrar alerta
			this.codeAlert = 'Código de verificación reenviado';

			// Ocultar la alerta
			setTimeout(_ => this.codeAlert = null, 5000); // 5 segundos

		}
	}

	onSubmit() {

		// if is invalid, show the form errors
		if (this.registerForm.invalid || this.emailDuplicated) {

			this.touched = false;
			return;
		}

		const registerEmailDto = new RegisterEmailModel();
		registerEmailDto.id = this.user.id;
		registerEmailDto.email = this.email.value?.trim();
		registerEmailDto.code = this.codeConfirmation.value?.trim();
		registerEmailDto.requestAt = this.requestAt;

		this.sharedService.setLoading(true);

		const session = this.apiAccountService.getSession();

		this.apiAccountService.registerEmail(registerEmailDto).then(
			(result: RegisterEmailModel) => {

				this.sharedService.setLoading(false);
				session.user.phoneVerifiedAt = result.emailVerifiedAt ?? new Date();
				SessionManager.setSession(session);
				this.sharedService.setShowVerifyAccountModal(false);

				this.sharedService.showSuccessAlert(
					null, `El email fue verificado correctamente`
				).then(_ => window.location.reload());

			},
			(error: any) => {

				this.sharedService.setLoading(false); // hide loader
				// display error
				const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error al actualizar la cuenta.');
				this.sharedService.showErrorAlert(null, errMsg);
			}
		);
	}

}
